import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ArticleCard from '@/components/feature/ArticleCard';
import HotRanking from '@/pages/home/components/HotRanking';
import SectionHeading from '@/components/feature/SectionHeading';
import { categories, getCategoryMeta } from '@/mocks/categories';
import { getArticlesByCategory, getHot, sortByHot, sortByTime } from '@/utils/articles';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

type SortKey = 'time' | 'hot' | 'share';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'time', label: '最新发布' },
  { key: 'hot', label: '最热浏览' },
  { key: 'share', label: '分享最多' },
];

const PAGE_SIZE = 4;

export default function CategoryPage() {
  const { type = 'news' } = useParams<{ type: string }>();
  const category = getCategoryMeta(type);

  const [sort, setSort] = useState<SortKey>('time');
  const [activeTag, setActiveTag] = useState<string>('全部');
  const [visible, setVisible] = useState(PAGE_SIZE);

  const baseList = getArticlesByCategory(type);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    baseList.forEach((item) => item.tags.forEach((tag) => set.add(tag)));
    return ['全部', ...Array.from(set)];
  }, [baseList]);

  const filtered = useMemo(() => {
    const list = activeTag === '全部'
      ? baseList
      : baseList.filter((item) => item.tags.includes(activeTag));
    if (sort === 'hot') return sortByHot(list);
    if (sort === 'share') return [...list].sort((a, b) => b.shares - a.shares);
    return sortByTime(list);
  }, [baseList, activeTag, sort]);

  const shown = filtered.slice(0, visible);
  const hotList = getHot(5);

  useDocumentMeta({
    title: category ? `${category.name} - 91吃瓜` : '分类 - 91吃瓜',
    description: category?.description,
    keywords: `91吃瓜,${category?.name ?? '娱乐资讯'},娱乐新闻,明星动态,网络热点`,
  });

  if (!category) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
          <i className="ri-compass-3-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading font-black text-2xl text-foreground-950">分类不存在</h1>
        <p className="mt-3 text-sm text-foreground-500">你访问的分类页面暂时不可用</p>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
      <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 flex-wrap">
        <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">{category.name}</span>
      </nav>

      <header className="mt-5 flex items-start gap-4">
        <span className="w-14 h-14 shrink-0 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
          <i className={`${category.icon} text-2xl`} />
        </span>
        <div>
          <h1 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">
            {category.name}
          </h1>
          <p className="mt-2 text-sm text-foreground-500 max-w-2xl">{category.description}</p>
        </div>
      </header>

      <div className="mt-7 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
        <div className="lg:col-span-2">
          <div className="rounded-lg bg-background-50 border border-background-200 p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setSort(option.key);
                      setVisible(PAGE_SIZE);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sort === option.key
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-primary-600'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <span className="text-xs text-foreground-400 whitespace-nowrap">
                共 {filtered.length} 篇
              </span>
            </div>

            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {allTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => {
                    setActiveTag(tag);
                    setVisible(PAGE_SIZE);
                  }}
                  className={`px-2.5 py-1 rounded text-xs transition-colors cursor-pointer whitespace-nowrap ${
                    activeTag === tag
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-800'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {shown.length === 0 ? (
            <div className="mt-6 rounded-lg bg-background-50 border border-background-200 py-16 text-center">
              <span className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
                <i className="ri-inbox-2-line text-2xl" />
              </span>
              <p className="mt-4 text-sm text-foreground-600">当前筛选条件下没有匹配的内容</p>
              <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => {
                    setActiveTag('全部');
                    setSort('time');
                  }}
                  className="px-4 py-2 rounded-md bg-background-100 text-foreground-800 border border-background-200 text-sm font-medium hover:border-primary-300 cursor-pointer whitespace-nowrap"
                >
                  重置筛选
                </button>
                <Link
                  to="/"
                  className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 cursor-pointer whitespace-nowrap"
                >
                  返回首页
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="mt-5 flex flex-col gap-4">
                {shown.map((article) => (
                  <ArticleCard key={article.id} article={article} variant="row" />
                ))}
              </div>

              <div className="mt-6 flex items-center justify-center">
                {visible < filtered.length ? (
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-background-50 border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line" />
                    加载更多
                  </button>
                ) : (
                  <span className="text-xs text-foreground-400">已加载全部 {filtered.length} 篇内容</span>
                )}
              </div>
            </>
          )}
        </div>

        <aside className="flex flex-col gap-8">
          <div>
            <SectionHeading title="热门内容榜" icon="ri-fire-line" />
            <HotRanking articles={hotList} />
          </div>
          <div>
            <SectionHeading title="其他频道" icon="ri-apps-2-line" />
            <div className="flex flex-col gap-3">
              {categories
                .filter((item) => item.key !== category.key)
                .map((item) => (
                  <Link
                    key={item.key}
                    to={item.path}
                    className="flex items-center gap-3 p-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center">
                      <i className={`${item.icon} text-lg`} />
                    </span>
                    <span className="text-sm font-medium text-foreground-800">{item.name}</span>
                    <i className="ri-arrow-right-line ml-auto text-foreground-300" />
                  </Link>
                ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}